import {
  searchReportingRouteSchemas,
  type SearchReportingRoute,
  type SearchReportingRouteInput,
  type SearchReportingRouteOutput,
} from '@charitystack/org-engagement-contracts';

const shortRoutes = new Set<SearchReportingRoute>([
  'GET /validate/transaction',
  'GET /validate/contact',
  'GET /validate/subscriptions',
  'GET /validate/form',
  'GET /validate/payout',
  'GET /validate/element',
  'POST /selection-summary',
  'POST /contacts/donation-years',
  'GET /notifications',
]);

export function isShortSearchReportingRoute(route: SearchReportingRoute): boolean {
  return shortRoutes.has(route);
}

export function createSearchReportingClient(options: {
  longReadUrl: string;
  shortReadUrl: string;
  authorization: string;
  fetch?: typeof fetch;
}) {
  const request = options.fetch ?? fetch;
  const urlFor = <R extends SearchReportingRoute>(
    route: R,
    input: SearchReportingRouteInput<R>,
  ) => {
    const schema = searchReportingRouteSchemas[route];
    const parsedInput = schema.input.parse(input);
    const [method, path] = route.split(' ') as ['GET' | 'POST', string];
    const base = isShortSearchReportingRoute(route) ? options.shortReadUrl : options.longReadUrl;
    const url = new URL(`${base.replace(/\/$/, '')}${path}`);
    if (method === 'GET') {
      for (const [key, value] of Object.entries(parsedInput)) {
        if (value !== undefined)
          url.searchParams.set(key, Array.isArray(value) ? value.join(',') : String(value));
      }
    }
    return url;
  };
  return {
    urlFor,
    async query<R extends SearchReportingRoute>(
      route: R,
      input: SearchReportingRouteInput<R>,
    ): Promise<SearchReportingRouteOutput<R>> {
      const schema = searchReportingRouteSchemas[route];
      const parsedInput = schema.input.parse(input);
      const [method] = route.split(' ') as ['GET' | 'POST', string];
      const url = urlFor(route, parsedInput);
      const init: RequestInit = {
        method,
        headers: {
          authorization: options.authorization,
          'x-cs-authorization': options.authorization,
          ...(method === 'POST' ? { 'content-type': 'application/json' } : {}),
        },
      };
      if (method === 'POST') init.body = JSON.stringify(parsedInput);
      const response = await request(url, init);
      if (!response.ok) throw new Error(`SEARCH_REPORTING_REQUEST_FAILED:${response.status}`);
      return schema.output.parse(await response.json()) as SearchReportingRouteOutput<R>;
    },
  };
}
